// FILECONTENT_START src/common/components/DataGrid/useGridInlineEdit.ts
import { blue } from "@mui/material/colors";
import { createTheme } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import I18n from "i18n-js";
import { globalMessageHandler } from "../GlobalMessaging";
import { GridApiRef, GridEventListener, GridEvents } from "./grid-imports";

const defaultTheme = createTheme();

const useStyles = makeStyles(
  theme => ({
    actions: {
      color: theme.palette.text.secondary,
    },
    textPrimary: {
      color: theme.palette.text.primary,
    },
    wrapCellTextRoot: {
      "& .MuiDataGrid-virtualScrollerContent": {
        height: "auto!important",
      },
      "& .MuiDataGrid-virtualScrollerRenderZone": {
        position: "relative",
      },
      "& .MuiDataGrid-row": {
        maxHeight: "unset!important",
      },
      "& .MuiDataGrid-cell": {
        maxHeight: "unset!important",
        whiteSpace: "pre-wrap",
        "& .MuiDataGrid-cellContent": {
          whiteSpace: "pre-wrap",
        },
      },
      "& .MuiDataGrid-cell--editing": {
        backgroundColor: `${blue[50]}!important`,
        /*theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.1)!important"
            : "rgba(0,0,0,0.1)!important",*/
        border: `solid 1px ${blue[100]}`,
        //borderTop: `solid 3px ${theme.palette.primary.main}`,
        "& .MuiDataGrid-editInputCell": {
          fontSize: "0.875rem",
          "& .MuiInputBase-input": {
            padding: "5px 8px;",
          },
        },
      },
      "& .Mui-error": {
        border: `1px solid ${theme.palette.error.main}`,
        backgroundColor: `rgb(126,10,15, ${
          theme.palette.mode === "dark" ? 0.2 : 0.1
        })!important`,
        color:
          theme.palette.mode === "dark"
            ? theme.palette.error.main
            : theme.palette.error.main,
      },
    },
    root: {
      /*"& .MuiDataGrid-cell--editable": {
        backgroundColor:
          theme.palette.mode === "dark" ? "#376331" : "rgb(217 243 190)",
      },*/
      "& .MuiDataGrid-cell--editing": {
        backgroundColor: `${blue[50]}!important`,
        /*theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.1)!important"
            : "rgba(0,0,0,0.1)!important",*/
        border: `solid 1px ${blue[100]}`,
        //borderTop: `solid 3px ${theme.palette.primary.main}`,
        "& .MuiDataGrid-editInputCell": {
          fontSize: "0.875rem",
          "& .MuiInputBase-input": {
            padding: "5px 8px;",
          },
        },
      },
      "& .Mui-error": {
        border: `1px solid ${theme.palette.error.main}`,
        backgroundColor: `rgb(126,10,15, ${
          theme.palette.mode === "dark" ? 0.2 : 0.1
        })!important`,
        color:
          theme.palette.mode === "dark"
            ? theme.palette.error.main
            : theme.palette.error.main,
      },
    },
  }),
  { defaultTheme }
);

export interface UseEditToolbarProps {
  apiRef?: GridApiRef;
}

export function useEditToolbar(props: UseEditToolbarProps) {
  const { apiRef } = props;

  const handleClick = () => {
    let firstEditableColumn = props.apiRef?.current
      .getAllColumns()
      .find(col => col.editable);
    if (!firstEditableColumn) {
      return;
    }
    const field = firstEditableColumn?.field || "";
    const id = Math.round(Math.random() * 100000);
    apiRef?.current.updateRows([{ id, isNew: true }]);
    apiRef?.current.setRowMode(id, "edit");
    // Wait for the grid to render with the new row

    setTimeout(() => {
      apiRef?.current.scrollToIndexes({
        rowIndex: apiRef?.current.getRowsCount() - 1,
      });

      apiRef?.current.setCellFocus(id, field);
    });
  };

  return {
    handleClick,
  };
}
export interface UseGridInlineEditProps {
  apiRef: GridApiRef;
  updateItem?: (id: any, item: any) => Promise<any>;
  createItem?: (item: any) => Promise<any>;
}

export default function useGridInlineEdit({
  apiRef,
  updateItem,
  createItem,
}: UseGridInlineEditProps) {
  const classes = useStyles();

  const handleRowEditStart: GridEventListener<GridEvents.rowEditStart> = (
    params,
    event
  ) => {
    event.defaultMuiPrevented = true;
  };

  const handleRowEditStop: GridEventListener<GridEvents.rowEditStop> = (
    params,
    event
  ) => {
    event.defaultMuiPrevented = true;
  };

  const handleCellFocusOut: GridEventListener<GridEvents.cellFocusOut> = (
    params,
    event
  ) => {
    event.defaultMuiPrevented = true;
  };

  const handleEditClick = (id: any) => (event: any) => {
    event.stopPropagation();
    apiRef.current.setRowMode(id, "edit");
  };

  const handleSaveClick = (id: any) => async (event: any) => {
    event.stopPropagation();
    const oldRow = apiRef.current.getRow(id);
    const editRowsModel = apiRef.current.getEditRowsModel();
    const _item = editRowsModel[id];
    let item: any = {};
    Object.keys(_item).forEach(key => {
      item[key] = _item[key].value;
    });

    if (updateItem && createItem) {
      const { error, data, success } = oldRow?.isNew
        ? await createItem(item)
        : await updateItem(id, item);
      if (success) {
        if (oldRow?.isNew) {
          //apiRef.current.commitRowChange(id);
          apiRef.current.updateRows([{ ...oldRow, _action: "delete" }]);
          //apiRef.current.setRowMode(id, "view");
          //const row = apiRef.current.getRow(id);
          apiRef.current.updateRows([data]);
        } else {
          apiRef.current.commitRowChange(id);
          apiRef.current.setRowMode(id, "view");
          //const row = apiRef.current.getRow(id);
          apiRef.current.updateRows([data]);
        }
        globalMessageHandler.success(I18n.t("App.successSaved"));
      } else if (typeof error === "object") {
        //const field = "email";

        let errors: any = {};
        let errorMessages: string[] = [];
        Object.keys(error).forEach(field => {
          errorMessages.push(error[field].join(", "));
          errors[field] = { ...editRowsModel[id][field], error: true };
        });

        apiRef.current.setEditRowsModel({
          ...editRowsModel,
          [id]: {
            ...editRowsModel[id],
            //[field]: { ...editRowsModel[id][field], error: true },
            ...errors,
          },
        });
        globalMessageHandler.error(errorMessages.join("\n"));
      } else if (typeof error === "string") {
        globalMessageHandler.error(error);
      }
    } else {
      apiRef.current.commitRowChange(id);
      apiRef.current.setRowMode(id, "view");
      const row = apiRef.current.getRow(id);
      apiRef.current.updateRows([{ ...row, isNew: false }]);
    }
  };

  const handleDeleteClick = (id: any) => (event: any) => {
    event.stopPropagation();
    apiRef.current.updateRows([{ id, _action: "delete" }]);
  };

  const handleCancelClick = (id: any) => (event: any) => {
    event.stopPropagation();
    apiRef.current.setRowMode(id, "view");

    const row = apiRef.current.getRow(id);
    if (row!.isNew) {
      apiRef.current.updateRows([{ id, _action: "delete" }]);
    }
  };

  return {
    classes,
    handleRowEditStart,
    handleRowEditStop,
    handleCellFocusOut,
    handleEditClick,
    handleSaveClick,
    handleDeleteClick,
    handleCancelClick,
  };
}

// FILECONTENT_END src/common/components/DataGrid/useGridInlineEdit.ts